import {
  ConnectionDataSources,
  ConnectionDataSourcesValues,
} from "src/baseConnectionNode/types";
import { AgentNodeData, BeMappedNode } from "src/constants/NodeDataTypes";
import { CONNECT_NODE_TYPES, NODE_TYPE } from "src/constants/NodeTypes";
import { LongTestRunConfigForm } from "src/flowContainer/RunFlowButton/LongTestRunsConfig";
import {
  JobConfigurationExecutionMode,
  JobRun,
  JobRunStatusType,
} from "src/jobs/types";

const longPauseNodeTypes: NODE_TYPE[] = [
  NODE_TYPE.AI_NODE_V2,
  NODE_TYPE.AGENT_NODE,
  NODE_TYPE.WEBHOOK_CONNECTION_NODE,
  NODE_TYPE.REVIEW_CONNECTION_NODE,
] as const;

const asyncNodeTypes: NODE_TYPE[] = [
  ...longPauseNodeTypes,
  ...CONNECT_NODE_TYPES,
] as const;

const connectNodeTypes: NODE_TYPE[] = [...CONNECT_NODE_TYPES] as const;

const hasLongPauseNode = (nodes: BeMappedNode[]) => {
  return nodes.some((node) => longPauseNodeTypes.includes(node.type));
};

const hasManyAsyncNodes = (nodes: BeMappedNode[]) => {
  return nodes.filter((node) => asyncNodeTypes.includes(node.type)).length > 5;
};

const hasConnectNode = (nodes: BeMappedNode[]) => {
  return nodes.some((node) => connectNodeTypes.includes(node.type));
};

export const hasAgentNodeWithLiveData = (nodes: BeMappedNode[]) => {
  return nodes.some((node) => {
    if (node.type !== NODE_TYPE.AGENT_NODE) return false;
    const meta = node.data as AgentNodeData;
    return (
      node.type === NODE_TYPE.AGENT_NODE &&
      meta?.config?.environments_config?.[
        ConnectionDataSources.authoring_mode_data_source
      ] === ConnectionDataSourcesValues.production
    );
  });
};

const getSuggestedMaxConcurrency = (
  nodes: BeMappedNode[],
): [JobConfigurationExecutionMode, number] => {
  const executionMode =
    hasLongPauseNode(nodes) || hasManyAsyncNodes(nodes) ? "async" : "sync";

  if (executionMode === "async") {
    return [executionMode, 5];
  }

  if (hasConnectNode(nodes)) {
    return [executionMode, 25];
  }

  return [executionMode, 50];
};

export const getSuggestedConcurrenyLimit = (
  datasetSize: number,
  nodes: BeMappedNode[],
): Pick<LongTestRunConfigForm, "executionMode" | "executionConcurrency"> => {
  const [executionMode, maxConcurrency] = getSuggestedMaxConcurrency(nodes);
  const adjustedDatasetSize = datasetSize === 0 ? 1 : datasetSize;
  return {
    executionMode,
    executionConcurrency: Math.min(
      Math.ceil(adjustedDatasetSize / 2000),
      maxConcurrency,
    ),
  };
};

export const isJobRunRunning = (jobRun: JobRun) => {
  switch (jobRun.status.type) {
    case JobRunStatusType.Running:
    case JobRunStatusType.Writing:
      return true;
    default:
      return false;
  }
};

export const isJobRunCompleted = (jobRun: JobRun) => {
  switch (jobRun.status.type) {
    case JobRunStatusType.Completed:
    case JobRunStatusType.Failed:
    case JobRunStatusType.Stopped:
      return true;
    default:
      return false;
  }
};
